import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '../use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useGetSiteConfigs } from '../use-site-configs';
import { ApiBackendInfo } from '../athena/types';
import { CwcJackpotSetting } from './types';

/**
 * 取得 CwcJackpot 活動設定
 * @param apiBackendInfo 
 * @returns CwcJackpotSetting
 */
const fetchCwcJackpotSetting = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<CwcJackpotSetting>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/CwcJackpot/GetSetting`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useCwcJackpotSetting = () => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.CWC_JACKPOT_SETTING,
			apiBackendInfo.url,
			apiBackendInfo.token
		],
		queryFn: () => fetchCwcJackpotSetting(apiBackendInfo),
		staleTime: 3 * 60 * 1000,    // 3 minutes
		cacheTime: 3 * 60 * 1000,   // 3 minutes
		enabled: Boolean(siteConfig?.EnableCwcJackpot) && apiBackendInfo.isReady
	});

	const isCwcJackpotSupportedByLeague = (sportId: number, leagueId: number) => {
		const setting = data?.Data; 
		return Boolean(
			siteConfig?.EnableCwcJackpot &&
			setting?.SportId === sportId &&
			setting?.LeagueIds?.includes(leagueId)
		);
	};

	const isCwcJackpotSupportedByBetType = (sportId: number, leagueId: number, betTypeId: number) => {
		const setting = data?.Data; 
		return Boolean(
			isCwcJackpotSupportedByLeague(sportId, leagueId) &&
			setting?.BetTypeIds?.includes(betTypeId)
		);
	};

	const convertAmountByCurrency = (amount: number, userCurrencyId: number) => {
		const currId = userCurrencyId ?? 2; // default to RM (馬幣)
		const currencySetting = data?.Data?.CurrencySettings?.find(setting => setting.CurrencyId === currId);
		const result = amount / (currencySetting?.ActualRate || 1);
		return Number((isNaN(result) ? 0 : result).toFixed(2));
	};

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch,
		isCwcJackpotSupportedByLeague,
		isCwcJackpotSupportedByBetType,
		convertAmountByCurrency
	};
}; 